import { DomainError } from "./errors";
import { isUf, type Uf } from "./dashboard";
import { isValidCNPJ, isValidCPF, normalizeDocument, onlyDigits } from "./documents";
import type { Crt, IcmsResult, StResult, TaxItemResult, TaxResult, IbsCbsPart } from "./tax";
import { rastroOf, type NfeLot, type NfeRastro } from "./lots";

// ===== NF-e modelo 55 de saída (RF-VEN-03; T2.3) =====
//
// Função pura: documento + resultado do motor de impostos (T2.2) → `infNFe` no JSON da Nuvem Fiscal (nomes de campos
// do leiaute 4.00 + NT 2025.002), com valores em número decimal. Centavos/pct4/q4 só viram decimal aqui, na borda.
// Os totais (ICMSTot, IBSCBSTot) são a soma dos campos dos itens e são conferidos com os totais do motor e com a
// fórmula do vNF — qualquer divergência = erro interno (a nota não sai com total que a SEFAZ recusaria).

/** Código IBGE da UF (cUF; dois primeiros dígitos da chave de acesso). */
export const UF_IBGE: Readonly<Record<Uf, number>> = {
  RO: 11,
  AC: 12,
  AM: 13,
  RR: 14,
  PA: 15,
  AP: 16,
  TO: 17,
  MA: 21,
  PI: 22,
  CE: 23,
  RN: 24,
  PB: 25,
  PE: 26,
  AL: 27,
  SE: 28,
  BA: 29,
  MG: 31,
  ES: 32,
  RJ: 33,
  SP: 35,
  PR: 41,
  SC: 42,
  RS: 43,
  MS: 50,
  MT: 51,
  GO: 52,
  DF: 53,
};

/** Texto que a SEFAZ exige no nome do destinatário em homologação (rejeição 598). */
export const HOMOLOG_RECIPIENT_NAME = "NF-E EMITIDA EM AMBIENTE DE HOMOLOGACAO - SEM VALOR FISCAL";
export const NFE_MAX_ITEMS = 200;
export const NFE_VERSION = "4.00";
export const NFE_SOFTWARE = "CX ERP 1.0";

/** Formas de pagamento do leiaute (tPag). */
export const NFE_PAYMENT_TYPES: Readonly<Record<string, string>> = {
  "01": "Dinheiro",
  "02": "Cheque",
  "03": "Cartão de crédito",
  "04": "Cartão de débito",
  "05": "Crédito loja",
  "10": "Vale alimentação",
  "11": "Vale refeição",
  "12": "Vale presente",
  "13": "Vale combustível",
  "15": "Boleto bancário",
  "16": "Depósito bancário",
  "17": "Pix",
  "18": "Transferência bancária",
  "19": "Programa de fidelidade",
  "20": "Pix estático",
  "21": "Crédito em loja",
  "22": "Pagamento eletrônico não informado",
  "90": "Sem pagamento",
  "99": "Outros",
};
export const isNfePaymentType = (s: string | null): s is string => s !== null && s in NFE_PAYMENT_TYPES;

/**
 * SVC (contingência) da UF do emissor: tpEmis 6 = SVC-AN, 7 = SVC-RS (tabela do Portal Nacional, a conferir com o
 * contador). A troca automática fica para depois (dhCont/xJust); a T2.3 só deixa a informação pronta.
 */
const SVC_RS: readonly Uf[] = ["AM", "BA", "GO", "MA", "MS", "MT", "PE", "PR"];
export const svcOf = (uf: Uf): { name: "SVC-AN" | "SVC-RS"; emissionType: 6 | 7 } =>
  SVC_RS.includes(uf) ? { name: "SVC-RS", emissionType: 7 } : { name: "SVC-AN", emissionType: 6 };

// ---------- conversões na borda (inteiros → decimal do JSON) ----------

/** Centavos → reais (o double mais próximo de n/100 imprime com até 2 casas). */
export const centsToDecimal = (cents: number) => cents / 100;
/** pct4 (100% = 1_000_000) → percentual (12,5% → 12.5). */
export const pct4ToDecimal = (pct4: number) => pct4 / 10_000;
/** q4 → quantidade decimal. */
export const q4ToDecimal = (q4: number) => q4 / 10_000;

/** Data/hora em São Paulo no formato da NF-e (`AAAA-MM-DDThh:mm:ss-03:00`; sem horário de verão desde 2019). */
export function nfeDateTime(d: Date): string {
  return new Date(d.getTime() - 3 * 3_600_000).toISOString().slice(0, 19) + "-03:00";
}

// ---------- chave de acesso ----------

/** DV mód. 11 (pesos 2..9 da direita para a esquerda); letras do CNPJ alfanumérico valem código ASCII − 48. */
export function accessKeyDv(key43: string): number {
  let sum = 0;
  let w = 2;
  for (let i = key43.length - 1; i >= 0; i--) {
    sum += (key43.charCodeAt(i) - 48) * w;
    w = w === 9 ? 2 : w + 1;
  }
  const r = sum % 11;
  return r < 2 ? 0 : 11 - r;
}

export type AccessKeyParts = {
  uf: Uf;
  issuedAt: Date;
  cnpj: string;
  model: 55 | 65;
  series: number;
  number: number;
  emissionType: number;
  cnf: string;
};

/** Chave de 44 posições: cUF AAMM CNPJ mod série nNF tpEmis cNF cDV. */
export function accessKey(p: AccessKeyParts): string {
  const aamm = nfeDateTime(p.issuedAt).slice(2, 7).replace("-", "");
  const k43 =
    String(UF_IBGE[p.uf]) +
    aamm +
    normalizeDocument(p.cnpj).padStart(14, "0") +
    String(p.model) +
    String(p.series).padStart(3, "0") +
    String(p.number).padStart(9, "0") +
    String(p.emissionType) +
    p.cnf;
  return k43 + String(accessKeyDv(k43));
}

export const isNfeAccessKey = (k: string) =>
  /^\d{2}\d{4}[0-9A-Z]{12}\d{2}\d{2}\d{3}\d{9}\d\d{8}\d$/.test(k) &&
  accessKeyDv(k.slice(0, 43)) === Number(k[43]);

const SEQUENCES = new Set(["01234567", "12345678", "23456789", "98765432", "87654321", "76543210"]);

/** cNF que a SEFAZ recusa: igual ao nNF, todos iguais ou sequência crescente/decrescente (NT 2019.001). */
function badCnf(cnf: string, number: number): boolean {
  return (
    cnf === String(number % 100_000_000).padStart(8, "0") || /^(\d)\1{7}$/.test(cnf) || SEQUENCES.has(cnf)
  );
}

/**
 * Código numérico da chave (cNF, 8 dígitos): determinístico pelo id do documento e tentativa (FNV-1a 32 bits), para
 * repetir o passo do Workflow dar a mesma chave; ajustado para fora dos valores que a SEFAZ recusa.
 */
export function cnfOf(seed: string, number: number): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  let n = h % 100_000_000;
  let cnf = String(n).padStart(8, "0");
  while (badCnf(cnf, number)) {
    n = (n + 7_919) % 100_000_000;
    cnf = String(n).padStart(8, "0");
  }
  return cnf;
}

// ---------- entrada ----------

export type NfeAddress = {
  street: string;
  number: string;
  complement: string | null;
  district: string;
  /** Código IBGE do município (7 dígitos). */
  cityIbge: string;
  city: string;
  uf: string;
  cep: string | null;
};

export type NfeIssuer = {
  cnpj: string;
  legalName: string;
  tradeName: string | null;
  ie: string;
  im: string | null;
  cnae: string | null;
  crt: Crt;
  phone: string | null;
  address: NfeAddress;
};

export type NfeRecipient = {
  document: string;
  name: string;
  ieIndicator: 1 | 2 | 9;
  ie: string | null;
  email: string | null;
  phone: string | null;
  address: NfeAddress;
};

export type NfeItem = {
  code: string;
  description: string;
  ncm: string | null;
  cest: string | null;
  gtin: string | null;
  unit: string;
  qtyQ4: number;
  unitPriceCents: number;
  /** Lotes do item (T3.2, RF-SUP-07): grupo `rastro` (lote sem as duas datas vai no `infAdProd`). */
  lots?: NfeLot[];
};

export type NfeVolume = {
  quantity: number | null;
  species: string | null;
  brand: string | null;
  numbering: string | null;
  netWeightG: number | null;
  grossWeightG: number | null;
};

export type NfeTransport = {
  /** modFrete: 0 emitente, 1 destinatário, 2 terceiros, 3 próprio remetente, 4 próprio destinatário, 9 sem frete. */
  mode: 0 | 1 | 2 | 3 | 4 | 9;
  carrier: {
    document: string | null;
    name: string;
    ie: string | null;
    address: string | null;
    city: string | null;
    uf: string | null;
  } | null;
  vehicle: { plate: string; uf: string | null; rntc: string | null } | null;
  volumes: NfeVolume[];
};

export type NfePaymentInput = {
  /** tPag da forma de pagamento (null = 99 Outros com o nome). */
  sefazType: string | null;
  name: string;
  amountCents: number;
  /** AAAA-MM-DD. */
  dueDate: string;
};

export type NfeInput = {
  environment: 1 | 2;
  series: number;
  number: number;
  cnf: string;
  emissionType: 1;
  issuedAt: Date;
  exitAt: Date | null;
  /** Hoje em São Paulo (AAAA-MM-DD): separa pagamento à vista de a prazo. */
  today: string;
  natureName: string;
  /** finNFe: 1 normal, 4 devolução (T2.4). */
  purpose: 1 | 4;
  /** tpNF: 1 saída (padrão), 0 entrada (devolução de venda emitida pelo próprio vendedor). */
  operationType?: 0 | 1;
  /** NFref: chaves das NF-e referenciadas (obrigatória na devolução). */
  references?: string[];
  /** indPres. */
  presence: 0 | 1 | 2 | 3 | 4 | 5 | 9;
  issuer: NfeIssuer;
  recipient: NfeRecipient;
  items: NfeItem[];
  taxes: TaxResult;
  transport: NfeTransport;
  payments: NfePaymentInput[];
  /** Natureza que não gera financeiro: pagamento 90 (sem pagamento). */
  noPayment: boolean;
  complementaryInfo: string | null;
  fiscoInfo: string | null;
  /** autXML (CPF/CNPJ autorizados a baixar o XML). */
  authorizedXml: string[];
  /** Número do pedido (xPed dos itens e nFat). */
  orderNumber: string | null;
};

// ---------- saída ----------

type Num = Record<string, number | string>;
export type NfeIcmsGroup = Record<string, Num>;
export type NfeDet = {
  nItem: number;
  prod: Record<string, string | number | NfeRastro[]>;
  imposto: {
    vTotTrib: number;
    ICMS: NfeIcmsGroup;
    IPI?: { cEnq: string; IPITrib?: Num; IPINT?: Num };
    PIS: Record<string, Num>;
    COFINS: Record<string, Num>;
    ICMSUFDest?: Num;
    IBSCBS?: { CST: string; cClassTrib: string; gIBSCBS?: Record<string, unknown> };
  };
  infAdProd?: string;
};
export type NfeDetPag = { indPag?: 0 | 1; tPag: string; xPag?: string; vPag: number };
export type NfeInfNFe = {
  versao: string;
  Id: string;
  ide: Record<string, string | number | { refNFe: string }[]>;
  emit: Record<string, unknown>;
  dest: Record<string, unknown>;
  autXML?: Record<string, string>[];
  det: NfeDet[];
  total: { ICMSTot: Record<string, number>; IBSCBSTot?: Record<string, unknown> };
  transp: Record<string, unknown>;
  cobr?: { fat: Record<string, string | number>; dup: { nDup: string; dVenc: string; vDup: number }[] };
  pag: { detPag: NfeDetPag[] };
  infAdic?: { infAdFisco?: string; infCpl?: string };
};

// ---------- validação dos dados cadastrais ----------

const IBGE_RE = /^\d{7}$/;

function addressProblems(a: NfeAddress, who: string): string[] {
  const p: string[] = [];
  if (!a.street.trim() || !a.number.trim() || !a.district.trim() || !a.city.trim())
    p.push(`${who}: endereço incompleto (logradouro, número, bairro e cidade)`);
  if (!IBGE_RE.test(a.cityIbge)) p.push(`${who}: código IBGE do município ausente (consulte o CEP de novo)`);
  else if (isUf(a.uf) && a.cityIbge.slice(0, 2) !== String(UF_IBGE[a.uf]))
    p.push(`${who}: o código IBGE do município não é da UF ${a.uf}`);
  if (!isUf(a.uf)) p.push(`${who}: UF inválida`);
  if (a.cep !== null && !/^\d{8}$/.test(a.cep)) p.push(`${who}: CEP inválido`);
  return p;
}

/**
 * O que falta para a nota sair (mensagens pt-BR para a tela). Vazio = pode montar o payload. Exportação (UF EX)
 * fica fora desta versão (o grupo `exporta` e o `idEstrangeiro` não são gerados).
 */
export function nfeProblems(i: Omit<NfeInput, "taxes" | "cnf" | "issuedAt" | "exitAt" | "today">): string[] {
  const p: string[] = [];
  if (!isValidCNPJ(i.issuer.cnpj)) p.push("Empresa: CNPJ inválido ou ausente");
  if (!/^(\d{2,14}|ISENTO)$/.test(i.issuer.ie)) p.push("Empresa: inscrição estadual ausente");
  if (!i.issuer.legalName.trim()) p.push("Empresa: razão social ausente");
  p.push(...addressProblems(i.issuer.address, "Empresa"));
  const doc = normalizeDocument(i.recipient.document);
  if (i.recipient.address.uf === "EX")
    p.push("Destinatário no exterior: exportação ainda não é emitida pelo sistema");
  else {
    if (!(doc.length === 11 ? isValidCPF(doc) : isValidCNPJ(doc)))
      p.push("Destinatário: CPF/CNPJ inválido ou ausente");
    p.push(...addressProblems(i.recipient.address, "Destinatário"));
  }
  if (!i.recipient.name.trim()) p.push("Destinatário: nome ausente");
  if (i.recipient.ieIndicator === 1 && !/^\d{2,14}$/.test(i.recipient.ie ?? ""))
    p.push("Destinatário contribuinte do ICMS sem inscrição estadual");
  if (i.items.length === 0) p.push("A nota precisa de ao menos um item");
  if (i.items.length > NFE_MAX_ITEMS) p.push(`A nota aceita até ${NFE_MAX_ITEMS} itens`);
  i.items.forEach((it, k) => {
    if (!/^\d{8}$/.test(it.ncm ?? "")) p.push(`Item ${k + 1} (${it.description}): NCM ausente ou inválido`);
    if (it.gtin !== null && !/^(\d{8}|\d{12,14})$/.test(it.gtin)) p.push(`Item ${k + 1}: GTIN inválido`);
  });
  if (!Number.isInteger(i.series) || i.series < 0 || i.series > 999 || (i.series >= 890 && i.series <= 899))
    p.push("Série inválida (0 a 999, fora de 890–899)");
  return p;
}

// ---------- grupos de impostos ----------

const d2 = centsToDecimal;
const p4 = pct4ToDecimal;

function own(icms: IcmsResult, reduction: boolean): Num {
  return {
    modBC: 3,
    ...(reduction ? { pRedBC: p4(icms.reductionPct4) } : {}),
    vBC: d2(icms.baseCents),
    pICMS: p4(icms.ratePct4),
    vICMS: d2(icms.valueCents),
  };
}

function fcp(icms: IcmsResult, withBase: boolean): Num {
  if (icms.fcpPct4 === 0) return {};
  return {
    ...(withBase ? { vBCFCP: d2(icms.fcpBaseCents) } : {}),
    pFCP: p4(icms.fcpPct4),
    vFCP: d2(icms.fcpCents),
  };
}

function st(s: StResult | null): Num {
  if (!s) return {};
  return {
    modBCST: s.modBc,
    ...(s.modBc === 4 ? { pMVAST: p4(s.mvaPct4) } : {}),
    ...(s.reductionPct4 ? { pRedBCST: p4(s.reductionPct4) } : {}),
    vBCST: d2(s.baseCents),
    pICMSST: p4(s.ratePct4),
    vICMSST: d2(s.valueCents),
    ...(s.fcpPct4 ? { vBCFCPST: d2(s.fcpBaseCents), pFCPST: p4(s.fcpPct4), vFCPST: d2(s.fcpCents) } : {}),
  };
}

function retained(icms: IcmsResult): Num {
  const r = icms.stRetained;
  if (!r) return {};
  return {
    vBCSTRet: d2(r.baseCents),
    pST: p4(r.ratePct4),
    vICMSSubstituto: d2(r.substituteOwnCents),
    vICMSSTRet: d2(r.valueCents),
  };
}

const credit = (icms: IcmsResult): Num => ({
  pCredSN: p4(icms.creditSnPct4),
  vCredICMSSN: d2(icms.creditSnCents),
});

/** Grupo de ICMS do item no leiaute (ICMS00…ICMS90, ICMSSN101…ICMSSN900). */
export function icmsGroup(icms: IcmsResult): NfeIcmsGroup {
  const head: Num =
    icms.cst !== null ? { orig: icms.origin, CST: icms.cst } : { orig: icms.origin, CSOSN: icms.csosn! };
  const hasOwn = icms.baseCents > 0 || icms.valueCents > 0;
  let body: Num;
  switch (icms.group) {
    case "ICMS00":
      body = { ...own(icms, false), ...fcp(icms, false) };
      break;
    case "ICMS10":
      body = { ...own(icms, false), ...fcp(icms, true), ...st(icms.st) };
      break;
    case "ICMS20":
      body = { ...own(icms, true), ...fcp(icms, true) };
      break;
    case "ICMS30":
    case "ICMSSN202":
      body = st(icms.st);
      break;
    case "ICMS51":
      body = {
        modBC: 3,
        ...(icms.reductionPct4 ? { pRedBC: p4(icms.reductionPct4) } : {}),
        vBC: d2(icms.baseCents),
        pICMS: p4(icms.ratePct4),
        vICMSOp: d2(icms.operationCents),
        pDif: p4(icms.deferralPct4),
        vICMSDif: d2(icms.deferredCents),
        vICMS: d2(icms.valueCents),
        ...fcp(icms, true),
      };
      break;
    case "ICMS60":
    case "ICMSSN500":
      body = retained(icms);
      break;
    case "ICMS70":
      body = { ...own(icms, true), ...fcp(icms, true), ...st(icms.st) };
      break;
    case "ICMS90":
      body = {
        ...(hasOwn ? { ...own(icms, icms.reductionPct4 > 0), ...fcp(icms, true) } : {}),
        ...st(icms.st),
      };
      break;
    case "ICMSSN101":
      body = credit(icms);
      break;
    case "ICMSSN201":
      body = { ...st(icms.st), ...credit(icms) };
      break;
    case "ICMSSN900":
      body = {
        ...(hasOwn ? own(icms, icms.reductionPct4 > 0) : {}),
        ...st(icms.st),
        ...(icms.creditSnCents ? credit(icms) : {}),
      };
      break;
    default:
      // ICMS40 (40, 41, 50) e ICMSSN102 (102, 103, 300, 400): só origem e CST/CSOSN.
      body = {};
  }
  return { [icms.group]: { ...head, ...body } };
}

function ipiGroup(i: TaxItemResult): NfeDet["imposto"]["IPI"] {
  const ipi = i.ipi;
  if (!ipi) return undefined;
  if (ipi.group === "IPINT") return { cEnq: ipi.enq, IPINT: { CST: ipi.cst } };
  const byUnit = ipi.qtyQ4 > 0 && ipi.ratePct4 === 0;
  return {
    cEnq: ipi.enq,
    IPITrib: byUnit
      ? { CST: ipi.cst, qUnid: q4ToDecimal(ipi.qtyQ4), vUnid: d2(ipi.unitCents), vIPI: d2(ipi.valueCents) }
      : { CST: ipi.cst, vBC: d2(ipi.baseCents), pIPI: p4(ipi.ratePct4), vIPI: d2(ipi.valueCents) },
  };
}

function pisCofinsGroup(r: TaxItemResult["pis"], tag: "PIS" | "COFINS"): Record<string, Num> {
  if (r.group === "NT") return { [`${tag}NT`]: { CST: r.cst } };
  return {
    [`${tag}${r.group}`]: {
      CST: r.cst,
      vBC: d2(r.baseCents),
      [`p${tag}`]: p4(r.ratePct4),
      [`v${tag}`]: d2(r.valueCents),
    },
  };
}

function difalGroup(i: TaxItemResult): Num | undefined {
  const d = i.difal;
  if (!d) return undefined;
  return {
    vBCUFDest: d2(d.baseCents),
    vBCFCPUFDest: d2(d.fcpBaseCents),
    pFCPUFDest: p4(d.fcpPct4),
    pICMSUFDest: p4(d.destRatePct4),
    pICMSInter: p4(d.interRatePct4),
    pICMSInterPart: p4(d.interPartPct4),
    vFCPUFDest: d2(d.fcpCents),
    vICMSUFDest: d2(d.destCents),
    vICMSUFRemet: d2(d.originCents),
  };
}

function ibsPart(part: IbsCbsPart, rateTag: string, valueTag: string): Record<string, unknown> {
  return {
    [rateTag]: p4(part.ratePct4),
    ...(part.deferralPct4 ? { gDif: { pDif: p4(part.deferralPct4), vDif: d2(part.deferredCents) } } : {}),
    ...(part.reductionPct4
      ? { gRed: { pRedAliq: p4(part.reductionPct4), pAliqEfet: p4(part.effectivePct4) } }
      : {}),
    [valueTag]: d2(part.valueCents),
  };
}

function ibsCbsGroup(i: TaxItemResult): NfeDet["imposto"]["IBSCBS"] {
  const g = i.ibsCbs;
  if (!g) return undefined;
  if (!g.hasValues) return { CST: g.cst, cClassTrib: g.classCode };
  return {
    CST: g.cst,
    cClassTrib: g.classCode,
    gIBSCBS: {
      vBC: d2(g.baseCents),
      gIBSUF: ibsPart(g.ibsUf, "pIBSUF", "vIBSUF"),
      gIBSMun: ibsPart(g.ibsMun, "pIBSMun", "vIBSMun"),
      vIBS: d2(g.ibsCents),
      gCBS: ibsPart(g.cbs, "pCBS", "vCBS"),
    },
  };
}

// ---------- totais ----------

const sumOf = (items: readonly TaxItemResult[], f: (i: TaxItemResult) => number) =>
  items.reduce((a, i) => a + f(i), 0);

const OWN_GROUPS = new Set(["ICMS00", "ICMS10", "ICMS20", "ICMS51", "ICMS70"]);
/** vBC que o grupo do item leva no XML (ICMS90/ICMSSN900 só quando há ICMS próprio). */
const emittedBase = (icms: IcmsResult) =>
  OWN_GROUPS.has(icms.group) ||
  ((icms.group === "ICMS90" || icms.group === "ICMSSN900") && (icms.baseCents > 0 || icms.valueCents > 0))
    ? icms.baseCents
    : 0;

/** ICMSTot em centavos, somado dos campos dos itens. */
export function icmsTotCents(items: readonly TaxItemResult[]) {
  const s = (f: (i: TaxItemResult) => number) => sumOf(items, f);
  return {
    vBC: s((i) => emittedBase(i.icms)),
    vICMS: s((i) => i.icms.valueCents),
    vICMSDeson: 0,
    vFCPUFDest: s((i) => i.difal?.fcpCents ?? 0),
    vICMSUFDest: s((i) => i.difal?.destCents ?? 0),
    vICMSUFRemet: s((i) => i.difal?.originCents ?? 0),
    vFCP: s((i) => i.icms.fcpCents),
    vBCST: s((i) => i.icms.st?.baseCents ?? 0),
    vST: s((i) => i.icms.st?.valueCents ?? 0),
    vFCPST: s((i) => i.icms.st?.fcpCents ?? 0),
    vFCPSTRet: 0,
    vProd: s((i) => i.grossCents),
    vFrete: s((i) => i.freightCents),
    vSeg: s((i) => i.insuranceCents),
    vDesc: s((i) => i.discountCents),
    vII: 0,
    vIPI: s((i) => i.ipi?.valueCents ?? 0),
    vIPIDevol: 0,
    vPIS: s((i) => i.pis.valueCents),
    vCOFINS: s((i) => i.cofins.valueCents),
    vOutro: s((i) => i.otherCents),
    vNF: s((i) => i.totalCents),
    vTotTrib: s((i) => i.approxTaxCents),
  };
}

/**
 * Confere os totais: Σ itens = totais do motor, campo a campo, e vNF = vProd − vDesc + vST + vFCPST + vFrete + vSeg +
 * vOutro + vIPI (fórmula da SEFAZ, rejeição 610). Divergência = `nfe_totals_mismatch` (nota não sai).
 */
export function checkNfeTotals(taxes: TaxResult): ReturnType<typeof icmsTotCents> {
  const t = icmsTotCents(taxes.items);
  const m = taxes.totals;
  const pairs: [string, number, number][] = [
    ["vBC", t.vBC, m.baseCents],
    ["vICMS", t.vICMS, m.icmsCents],
    ["vFCP", t.vFCP, m.fcpCents],
    ["vBCST", t.vBCST, m.stBaseCents],
    ["vST", t.vST, m.stCents],
    ["vFCPST", t.vFCPST, m.fcpStCents],
    ["vICMSUFDest", t.vICMSUFDest, m.difalCents],
    ["vFCPUFDest", t.vFCPUFDest, m.difalFcpCents],
    ["vICMSUFRemet", t.vICMSUFRemet, m.difalOriginCents],
    ["vProd", t.vProd, m.productsCents],
    ["vFrete", t.vFrete, m.freightCents],
    ["vSeg", t.vSeg, m.insuranceCents],
    ["vDesc", t.vDesc, m.discountCents],
    ["vOutro", t.vOutro, m.otherCents],
    ["vIPI", t.vIPI, m.ipiCents],
    ["vPIS", t.vPIS, m.pisCents],
    ["vCOFINS", t.vCOFINS, m.cofinsCents],
    ["vTotTrib", t.vTotTrib, m.approxTaxCents],
    ["vNF", t.vNF, m.invoiceCents],
  ];
  const bad = pairs.filter(([, a, b]) => a !== b).map(([k]) => k);
  const formula = t.vProd - t.vDesc + t.vST + t.vFCPST + t.vFrete + t.vSeg + t.vOutro + t.vIPI;
  if (formula !== t.vNF) bad.push("vNF (fórmula)");
  if (bad.length)
    throw new DomainError(
      "nfe_totals_mismatch",
      `Totais da nota não conferem com os itens: ${bad.join(", ")}`,
    );
  return t;
}

// ---------- pagamento e cobrança ----------

/**
 * detPag das parcelas: agrupadas por forma (tPag) e à vista/a prazo; diferença para o vNF (ST, IPI, FCP-ST) na última
 * forma (Σ vPag ≥ vNF, rejeição 869); excesso tirado das últimas. Natureza sem financeiro = 90 (sem pagamento).
 */
export function nfePayments(
  payments: readonly NfePaymentInput[],
  invoiceCents: number,
  noPayment: boolean,
  today: string,
): NfeDetPag[] {
  if (noPayment) return [{ tPag: "90", vPag: 0 }];
  type G = { indPag: 0 | 1; tPag: string; xPag?: string; cents: number };
  const groups: G[] = [];
  for (const p of payments) {
    if (p.amountCents <= 0) continue;
    const tPag = isNfePaymentType(p.sefazType) && p.sefazType !== "90" ? p.sefazType : "99";
    const xPag = tPag === "99" ? (p.name.trim() || "Outros").slice(0, 60).padEnd(2, ".") : undefined;
    const indPag: 0 | 1 = p.dueDate <= today ? 0 : 1;
    const g = groups.find((x) => x.tPag === tPag && x.indPag === indPag && x.xPag === xPag);
    if (g) g.cents += p.amountCents;
    else groups.push({ indPag, tPag, ...(xPag ? { xPag } : {}), cents: p.amountCents });
  }
  if (groups.length === 0) groups.push({ indPag: 0, tPag: "99", xPag: "Outros", cents: invoiceCents });
  let diff = invoiceCents - groups.reduce((a, g) => a + g.cents, 0);
  if (diff > 0) groups[groups.length - 1]!.cents += diff;
  for (let k = groups.length - 1; diff < 0 && k >= 0; k--) {
    const take = Math.min(groups[k]!.cents, -diff);
    groups[k]!.cents -= take;
    diff += take;
  }
  return groups
    .filter((g) => g.cents > 0)
    .map((g) => ({
      indPag: g.indPag,
      tPag: g.tPag,
      ...(g.xPag ? { xPag: g.xPag } : {}),
      vPag: d2(g.cents),
    }));
}

/** Cobrança (fatura + duplicatas) das parcelas a prazo; null quando tudo é à vista. */
export function nfeBilling(
  payments: readonly NfePaymentInput[],
  invoiceNumber: string,
  today: string,
): NfeInfNFe["cobr"] | undefined {
  const due = payments.filter((p) => p.amountCents > 0 && p.dueDate > today);
  if (due.length === 0) return undefined;
  const total = due.reduce((a, p) => a + p.amountCents, 0);
  return {
    fat: { nFat: invoiceNumber.slice(0, 60), vOrig: d2(total), vDesc: 0, vLiq: d2(total) },
    dup: due
      .slice()
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0))
      .map((p, k) => ({ nDup: String(k + 1).padStart(3, "0"), dVenc: p.dueDate, vDup: d2(p.amountCents) })),
  };
}

// ---------- montagem ----------

const clip = (s: string, n: number) => s.replace(/\s+/g, " ").trim().slice(0, n);
const opt = <K extends string>(k: K, v: string | null | undefined, n = 60): Partial<Record<K, string>> =>
  v && v.trim() ? ({ [k]: clip(v, n) } as Record<K, string>) : {};

function address(a: NfeAddress, phone: string | null): Record<string, string> {
  return {
    xLgr: clip(a.street, 60),
    nro: clip(a.number, 60),
    ...opt("xCpl", a.complement),
    xBairro: clip(a.district, 60),
    cMun: a.cityIbge,
    xMun: clip(a.city, 60),
    UF: a.uf,
    ...(a.cep ? { CEP: a.cep } : {}),
    cPais: "1058",
    xPais: "BRASIL",
    ...(phone && /^\d{6,14}$/.test(onlyDigits(phone)) ? { fone: onlyDigits(phone) } : {}),
  };
}

const docField = (doc: string): Record<string, string> => {
  const d = normalizeDocument(doc);
  return d.length === 11 ? { CPF: d } : { CNPJ: d };
};

function transport(t: NfeTransport, destination: 1 | 2 | 3): Record<string, unknown> {
  const c = t.carrier;
  return {
    modFrete: t.mode,
    ...(c
      ? {
          transporta: {
            ...(c.document ? docField(c.document) : {}),
            ...opt("xNome", c.name),
            ...(c.ie && /^(\d{2,14}|ISENTO)$/.test(c.ie) ? { IE: c.ie } : {}),
            ...opt("xEnder", c.address),
            ...opt("xMun", c.city),
            ...(c.uf && isUf(c.uf) ? { UF: c.uf } : {}),
          },
        }
      : {}),
    // Veículo só na operação interna (a SEFAZ recusa veicTransp na interestadual).
    ...(t.vehicle && destination === 1
      ? {
          veicTransp: {
            placa: normalizeDocument(t.vehicle.plate),
            ...(t.vehicle.uf && isUf(t.vehicle.uf) ? { UF: t.vehicle.uf } : {}),
            ...opt("RNTC", t.vehicle.rntc, 20),
          },
        }
      : {}),
    ...(t.volumes.length
      ? {
          vol: t.volumes.map((v) => ({
            ...(v.quantity !== null ? { qVol: v.quantity } : {}),
            ...opt("esp", v.species),
            ...opt("marca", v.brand),
            ...opt("nVol", v.numbering),
            ...(v.netWeightG !== null ? { pesoL: v.netWeightG / 1000 } : {}),
            ...(v.grossWeightG !== null ? { pesoB: v.grossWeightG / 1000 } : {}),
          })),
        }
      : {}),
  };
}

function ibsCbsTot(items: readonly TaxItemResult[]): Record<string, unknown> | undefined {
  if (!items.some((i) => i.ibsCbs)) return undefined;
  const s = (f: (i: TaxItemResult) => number) => d2(sumOf(items, f));
  return {
    vBCIBSCBS: s((i) => i.ibsCbs?.baseCents ?? 0),
    gIBS: {
      gIBSUF: {
        vDif: s((i) => i.ibsCbs?.ibsUf.deferredCents ?? 0),
        vDevTrib: 0,
        vIBSUF: s((i) => i.ibsCbs?.ibsUf.valueCents ?? 0),
      },
      gIBSMun: {
        vDif: s((i) => i.ibsCbs?.ibsMun.deferredCents ?? 0),
        vDevTrib: 0,
        vIBSMun: s((i) => i.ibsCbs?.ibsMun.valueCents ?? 0),
      },
      vIBS: s((i) => i.ibsCbs?.ibsCents ?? 0),
      vCredPres: 0,
      vCredPresCondSus: 0,
    },
    gCBS: {
      vDif: s((i) => i.ibsCbs?.cbs.deferredCents ?? 0),
      vDevTrib: 0,
      vCBS: s((i) => i.ibsCbs?.cbs.valueCents ?? 0),
      vCredPres: 0,
      vCredPresCondSus: 0,
    },
  };
}

/** Textos legais das regras (infAdProd vai no item; repetidos uma vez no infCpl) + texto da Lei 12.741. */
function complementary(i: NfeInput, totTribCents: number): string | undefined {
  const parts: string[] = [];
  if (i.complementaryInfo?.trim()) parts.push(i.complementaryInfo.trim());
  if (totTribCents > 0 && i.taxes.finalConsumer)
    parts.push(
      `Valor aproximado dos tributos: R$ ${(totTribCents / 100).toFixed(2).replace(".", ",")} (Lei 12.741/2012)`,
    );
  if (i.orderNumber) parts.push(`Pedido ${i.orderNumber}`);
  const text = clip(parts.join(" | "), 5000);
  return text || undefined;
}

/**
 * Monta o `infNFe`. Lança `nfe_incomplete` (dados cadastrais, ver `nfeProblems`) ou `nfe_totals_mismatch`. O número,
 * a série e o cNF vêm gravados no documento (o mesmo documento sempre dá a mesma chave).
 */
/**
 * Lotes do item (T3.2): `rastro` no `prod` e o texto dos lotes sem as duas datas somado às informações adicionais do
 * item (`infAdProd`, até 500 caracteres).
 */
function lotFields(
  it: NfeItem,
  infoText: string | null | undefined,
): { rastro: Record<string, NfeRastro[]>; info: Record<string, string> } {
  const r = rastroOf(it.lots ?? []);
  const info = [infoText?.trim(), r.note].filter(Boolean).join(" — ");
  return {
    rastro: r.rastro.length ? { rastro: r.rastro } : {},
    info: info ? { infAdProd: clip(info, 500) } : {},
  };
}

export function buildNfePayload(i: NfeInput): { accessKey: string; infNFe: NfeInfNFe } {
  const problems = nfeProblems(i);
  if (i.taxes.items.length !== i.items.length) problems.push("Impostos não correspondem aos itens");
  if (i.taxes.destination === 3) problems.push("Exportação ainda não é emitida pelo sistema");
  const refs = [...new Set(i.references ?? [])];
  if (refs.some((k) => !isNfeAccessKey(k))) problems.push("Chave de acesso referenciada inválida");
  if (i.purpose === 4 && refs.length === 0) problems.push("Devolução sem a NF-e referenciada");
  if (problems.length) throw new DomainError("nfe_incomplete", problems.join("; "));
  const tot = checkNfeTotals(i.taxes);
  const issuerUf = i.issuer.address.uf as Uf;
  const key = accessKey({
    uf: issuerUf,
    issuedAt: i.issuedAt,
    cnpj: i.issuer.cnpj,
    model: 55,
    series: i.series,
    number: i.number,
    emissionType: i.emissionType,
    cnf: i.cnf,
  });
  const presenceNeedsIntermed = [1, 2, 3, 4, 9].includes(i.presence);
  const ide: NfeInfNFe["ide"] = {
    cUF: UF_IBGE[issuerUf],
    cNF: i.cnf,
    natOp: clip(i.natureName, 60),
    mod: 55,
    serie: i.series,
    nNF: i.number,
    dhEmi: nfeDateTime(i.issuedAt),
    ...(i.exitAt ? { dhSaiEnt: nfeDateTime(i.exitAt) } : {}),
    tpNF: i.operationType ?? 1,
    idDest: i.taxes.destination,
    cMunFG: i.issuer.address.cityIbge,
    tpImp: 1,
    tpEmis: i.emissionType,
    cDV: Number(key[43]),
    tpAmb: i.environment,
    finNFe: i.purpose,
    indFinal: i.taxes.finalConsumer ? 1 : 0,
    indPres: i.presence,
    ...(presenceNeedsIntermed ? { indIntermed: 0 } : {}),
    procEmi: 0,
    verProc: NFE_SOFTWARE,
    ...(refs.length ? { NFref: refs.map((k) => ({ refNFe: k })) } : {}),
  };
  const emit = {
    CNPJ: normalizeDocument(i.issuer.cnpj),
    xNome: clip(i.issuer.legalName, 60),
    ...opt("xFant", i.issuer.tradeName),
    enderEmit: address(i.issuer.address, i.issuer.phone),
    IE: i.issuer.ie,
    ...(i.issuer.im?.trim()
      ? {
          IM: clip(i.issuer.im, 15),
          ...(i.issuer.cnae && /^\d{7}$/.test(i.issuer.cnae) ? { CNAE: i.issuer.cnae } : {}),
        }
      : {}),
    CRT: i.issuer.crt,
  };
  const r = i.recipient;
  const dest = {
    ...docField(r.document),
    xNome: i.environment === 2 ? HOMOLOG_RECIPIENT_NAME : clip(r.name, 60),
    enderDest: address(r.address, r.phone),
    indIEDest: r.ieIndicator,
    ...(r.ieIndicator === 1 && r.ie ? { IE: r.ie } : {}),
    ...(r.email && /^[^\s@]+@[^\s@]+$/.test(r.email) ? { email: r.email.trim().slice(0, 60) } : {}),
  };
  const det: NfeDet[] = i.taxes.items.map((t, k) => {
    const it = i.items[k]!;
    const gtin = it.gtin ?? "SEM GTIN";
    const ipi = ipiGroup(t);
    const difal = difalGroup(t);
    const ibs = ibsCbsGroup(t);
    const lot = lotFields(it, t.infoText);
    return {
      nItem: k + 1,
      prod: {
        cProd: clip(it.code, 60) || String(k + 1),
        cEAN: gtin,
        xProd: clip(it.description, 120),
        NCM: it.ncm!,
        ...(it.cest && /^\d{7}$/.test(it.cest) ? { CEST: it.cest } : {}),
        CFOP: t.cfop,
        uCom: clip(it.unit, 6) || "UN",
        qCom: q4ToDecimal(it.qtyQ4),
        vUnCom: d2(it.unitPriceCents),
        vProd: d2(t.grossCents),
        cEANTrib: gtin,
        uTrib: clip(it.unit, 6) || "UN",
        qTrib: q4ToDecimal(it.qtyQ4),
        vUnTrib: d2(it.unitPriceCents),
        ...(t.freightCents ? { vFrete: d2(t.freightCents) } : {}),
        ...(t.insuranceCents ? { vSeg: d2(t.insuranceCents) } : {}),
        ...(t.discountCents ? { vDesc: d2(t.discountCents) } : {}),
        ...(t.otherCents ? { vOutro: d2(t.otherCents) } : {}),
        indTot: 1,
        ...(i.orderNumber ? { xPed: clip(i.orderNumber, 15), nItemPed: k + 1 } : {}),
        ...lot.rastro,
      },
      imposto: {
        vTotTrib: d2(t.approxTaxCents),
        ICMS: icmsGroup(t.icms),
        ...(ipi ? { IPI: ipi } : {}),
        PIS: pisCofinsGroup(t.pis, "PIS"),
        COFINS: pisCofinsGroup(t.cofins, "COFINS"),
        ...(difal ? { ICMSUFDest: difal } : {}),
        ...(ibs ? { IBSCBS: ibs } : {}),
      },
      ...lot.info,
    };
  });
  const ICMSTot = Object.fromEntries(Object.entries(tot).map(([k, v]) => [k, d2(v)]));
  const ibsTot = ibsCbsTot(i.taxes.items);
  const cobr = nfeBilling(i.payments, i.orderNumber ?? String(i.number), i.today);
  const infCpl = complementary(i, tot.vTotTrib);
  const fisco = i.fiscoInfo?.trim() ? clip(i.fiscoInfo, 2000) : undefined;
  const aut = [...new Set(i.authorizedXml.map(normalizeDocument))].filter((d) =>
    d.length === 11 ? isValidCPF(d) : isValidCNPJ(d),
  );
  const infNFe: NfeInfNFe = {
    versao: NFE_VERSION,
    Id: `NFe${key}`,
    ide,
    emit,
    dest,
    ...(aut.length ? { autXML: aut.slice(0, 10).map(docField) } : {}),
    det,
    total: { ICMSTot, ...(ibsTot ? { IBSCBSTot: ibsTot } : {}) },
    transp: transport(i.transport, i.taxes.destination),
    ...(cobr && !i.noPayment ? { cobr } : {}),
    pag: { detPag: nfePayments(i.payments, tot.vNF, i.noPayment, i.today) },
    ...(infCpl || fisco
      ? { infAdic: { ...(fisco ? { infAdFisco: fisco } : {}), ...(infCpl ? { infCpl } : {}) } }
      : {}),
  };
  return { accessKey: key, infNFe };
}

/** Grupos reaproveitados pela NFC-e (T3.1, `nfce.ts`). */
export {
  lotFields as nfeLotFields,
  address as nfeAddressGroup,
  docField as nfeDocField,
  ipiGroup as nfeIpiGroup,
  pisCofinsGroup as nfePisCofinsGroup,
  ibsCbsGroup as nfeIbsCbsGroup,
  ibsCbsTot as nfeIbsCbsTot,
};
